import React, { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { Task, User } from '../lib/api';
import { SimpleBadge } from './ui/simple-badge';

interface TaskCardProps {
  task: Task;
  onStatusChange: (taskId: string, status: Task['status']) => void;
  onDelete: (taskId: string) => void;
  showLeadInfo?: boolean;
  onReassign?: (taskId: string, userId: string) => void;
  teamMembers?: User[];
}

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onStatusChange,
  onDelete,
  showLeadInfo = false,
  onReassign,
  teamMembers,
}) => {
  const [showReassign, setShowReassign] = useState(false);
  const isOverdue = new Date(task.dueDate) < new Date() && task.status !== 'COMPLETED';
  const isDueToday = new Date(task.dueDate).toDateString() === new Date().toDateString();

  const getStatusColor = () => {
    if (task.status === 'COMPLETED') return 'bg-green-100 text-green-800 border-green-200';
    if (isOverdue) return 'bg-red-100 text-red-800 border-red-200';
    if (isDueToday) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-blue-100 text-blue-800 border-blue-200';
  };

  const getPriorityColor = () => {
    switch (task.priority) {
      case 'URGENT': return 'bg-red-200 text-red-900 border-red-300';
      case 'HIGH': return 'bg-red-100 text-red-800 border-red-200';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'LOW': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <div className={`bg-white rounded-lg border p-4 ${isOverdue ? 'border-red-200' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          <input
            type="checkbox"
            checked={task.status === 'COMPLETED'}
            onChange={(e) => onStatusChange(task.id, e.target.checked ? 'COMPLETED' : 'PENDING')}
            className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            aria-label={`Mark "${task.title}" as ${task.status === 'COMPLETED' ? 'pending' : 'completed'}`}
          />
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <h4 className={`text-sm font-medium ${task.status === 'COMPLETED' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                {task.title}
              </h4>
              <SimpleBadge className={getPriorityColor()}>
                {task.priority}
              </SimpleBadge>
            </div>
            
            {task.description && (
              <p className={`text-sm mb-2 ${task.status === 'COMPLETED' ? 'line-through text-gray-400' : 'text-gray-600'}`}>
                {task.description}
              </p>
            )}
            
            {showLeadInfo && (task as any).lead && (
              <p className="text-xs text-gray-500 mb-2">
                📋 {(task as any).lead.name} - {(task as any).lead.company}
              </p>
            )}
            
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">
                📅 {formatDate(task.dueDate)}
              </span>
              <SimpleBadge className={getStatusColor()}>
                {task.status === 'COMPLETED' ? '✅ Completed' : 
                 isOverdue ? '🔴 Overdue' : 
                 isDueToday ? '🟡 Due Today' : '🔵 Upcoming'}
              </SimpleBadge>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Reassign dropdown (managers only) */}
          {onReassign && teamMembers && teamMembers.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setShowReassign(!showReassign)}
                className="text-xs px-3 py-1.5 rounded border border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 transition-colors flex items-center"
                aria-label={`Reassign task "${task.title}"`}
                title="Reassign this task to another team member"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Reassign
              </button>
              {showReassign && (
                <div className="absolute right-0 top-full mt-1 w-52 bg-white border rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                  {teamMembers
                    .filter((m) => m.id !== (task as any).userId && m.status === 'ACTIVE')
                    .map((member) => (
                      <button
                        key={member.id}
                        onClick={() => {
                          onReassign(task.id, member.id);
                          setShowReassign(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b last:border-b-0"
                      >
                        <span className="font-medium text-gray-800">{member.name}</span>
                        <span className="text-gray-400 ml-1 text-xs">{member.role}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}

          <button
            onClick={() => {
              if (window.confirm(`Delete task "${task.title}"? This cannot be undone.`)) {
                onDelete(task.id);
              }
            }}
            className="text-gray-400 hover:text-red-500 transition-colors"
            aria-label={`Delete task "${task.title}"`}
            title="Delete task"
          >
            🗑️
          </button>
        </div>
      </div>
    </div>
  );
};